/**
 * `permission_role_defaults` — the layer BENEATH the group rows.
 *
 * MOVED HERE, NOT REBUILT
 * -----------------------
 * Lifted from `UsersAdminPage`'s `?tab=defaults`. Everything survives:
 * the admin card read-only with its note (the role short-circuits
 * `has_permission`, so its cells decide nothing), only the touched
 * `user` cells are sent on save, the count in the Save button.
 *
 * It lives on the Grupos screen because it is what an EMPTY cell of the
 * Contenido matrix means, and the matrix already draws its glyph inside
 * every `default` cell. It is not a person and not an ámbito, so
 * Personas and Ámbitos are both the wrong home.
 *
 * TWO CORRECTIONS ON THE WAY OVER
 * -------------------------------
 * 1. **`Boolean(row?.granted)` is gone.** A role default with no row is
 *    UNKNOWN, not denied, and the difference was reachable rather than
 *    theoretical: `dashboard.send_report` rows exist in the database
 *    (portal migration 040 seeds both roles) and were absent from the
 *    frontend's vocabulary until 2026-09-07, so the constant and the
 *    data already disagreed. A missing row renders as a warning-toned
 *    `?` naming the (role, section, action).
 * 2. **`send_report` is drawn.** The card iterates
 *    `ACTIONS_BY_SECTION`, which now carries it, so who may schedule a
 *    send is visible and editable in the only console that administers
 *    it.
 */

import { Badge, Button, Card } from "@omnitok/ui";
import { Callout } from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { AlertTriangle, Save } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useT } from "@/i18n";
import {
  ACTIONS_BY_SECTION,
  PERMISSION_SECTIONS,
  adminPermissionsApi,
  type PermissionAction,
  type PermissionSection,
  type RoleDefault,
  type RoleDefaultInput,
} from "@/lib/admin-users-api";
import { permissionActionLabel } from "@/lib/permission-labels";

/** `true` | `false` | `null` for "there is no row". Exported so a test
 *  can assert the third case never collapses into the second. */
export function storedDefault(
  rows: readonly RoleDefault[],
  role: string,
  section: string,
  action: string,
): boolean | null {
  const hit = rows.find(
    (d) => d.role === role && d.section === section && d.action === action,
  );
  return hit === undefined ? null : hit.granted;
}

export function RoleDefaultsPane(): JSX.Element {
  const { t } = useT();
  const [rows, setRows] = useState<RoleDefault[]>([]);
  const [pending, setPending] = useState<Map<string, boolean>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setRows(await adminPermissionsApi.listRoleDefaults());
      setPending(new Map());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const userValue = useCallback(
    (section: string, action: string): boolean | null => {
      const staged = pending.get(`${section}::${action}`);
      if (staged !== undefined) return staged;
      return storedDefault(rows, "user", section, action);
    },
    [pending, rows],
  );

  const missing = useMemo(() => {
    const out: string[] = [];
    for (const section of PERMISSION_SECTIONS) {
      for (const action of ACTIONS_BY_SECTION[section]) {
        for (const role of ["admin", "user"]) {
          if (storedDefault(rows, role, section, action) === null) {
            out.push(`${role}.${section}.${action}`);
          }
        }
      }
    }
    return out;
  }, [rows]);

  const save = async (): Promise<void> => {
    if (pending.size === 0) return;
    const updates: RoleDefaultInput[] = [];
    for (const [key, granted] of pending.entries()) {
      const [section, action] = key.split("::") as [
        PermissionSection,
        PermissionAction,
      ];
      updates.push({ role: "user", section, action, granted });
    }
    setSaving(true);
    setError(null);
    try {
      setRows(await adminPermissionsApi.updateRoleDefaults(updates));
      setPending(new Map());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-neutral-500">
        <Spinner size={16} ariaLabel={t("common.loading")} />
        {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-neutral-600">
          {t("access.defaults.subtitle")}
        </p>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Save size={14} />}
          disabled={pending.size === 0 || saving}
          loading={saving}
          onClick={() => void save()}
        >
          {t("access.defaults.save", { count: pending.size })}
        </Button>
      </div>

      {error !== null && (
        <Callout tone="danger" title={t("access.common.errorTitle")}>
          <span className="whitespace-pre-wrap">{error}</span>
        </Callout>
      )}

      {missing.length > 0 && (
        <Callout tone="warning" title={t("access.defaults.missingTitle")}>
          {t("access.defaults.missing", { cells: missing.join(", ") })}
        </Callout>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card variant="bordered" noPadding>
          <header className="flex items-center gap-2 border-b border-neutral-200 bg-neutral-50 px-4 py-2">
            <span className="text-sm font-semibold text-neutral-800">admin</span>
            <Badge variant="neutral" size="sm" pill>
              {t("access.defaults.readonly")}
            </Badge>
          </header>
          <div className="space-y-3 p-4">
            <p className="text-xs text-warning-dark">
              {t("access.defaults.adminNote")}
            </p>
            {PERMISSION_SECTIONS.map((section) => (
              <div key={section}>
                <p className="text-xs font-semibold text-neutral-500">
                  {section}
                </p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {ACTIONS_BY_SECTION[section].map((action) => (
                    // A `<span>`, not a disabled `<button>`: the page
                    // this comes from already makes this distinction on
                    // purpose, and a disabled control would still assert
                    // "this is editable, just not now".
                    <DefaultChip
                      key={action}
                      as="span"
                      label={permissionActionLabel(t, action)}
                      value={storedDefault(rows, "admin", section, action)}
                      section={section}
                      action={action}
                      t={t}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card variant="bordered" noPadding>
          <header className="border-b border-neutral-200 bg-neutral-50 px-4 py-2">
            <span className="text-sm font-semibold text-neutral-800">user</span>
          </header>
          <div className="space-y-3 p-4">
            {PERMISSION_SECTIONS.map((section) => (
              <div key={section}>
                <p className="text-xs font-semibold text-neutral-500">
                  {section}
                </p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {ACTIONS_BY_SECTION[section].map((action) => (
                    <DefaultChip
                      key={action}
                      as="button"
                      label={permissionActionLabel(t, action)}
                      value={userValue(section, action)}
                      section={section}
                      action={action}
                      t={t}
                      onToggle={() =>
                        setPending((prev) => {
                          const next = new Map(prev);
                          // An UNKNOWN cell toggles to `true`: there is
                          // no row to invert, and writing one is exactly
                          // what fixes the unknown.
                          next.set(
                            `${section}::${action}`,
                            userValue(section, action) !== true,
                          );
                          return next;
                        })
                      }
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

/**
 * One default cell, in three states.
 *
 * `null` is the third and it is the reason this component exists: the
 * page it comes from wrote `Boolean(row?.granted)` and rendered "denied"
 * for a row nobody has ever written.
 */
export function DefaultChip({
  as,
  label,
  value,
  section,
  action,
  t,
  onToggle,
}: {
  as: "span" | "button";
  label: string;
  value: boolean | null;
  section: string;
  action: string;
  t: (key: string, params?: Record<string, string | number>) => string;
  onToggle?: () => void;
}): JSX.Element {
  const tone =
    value === null
      ? "border-warning/40 bg-warning/10 text-warning-dark"
      : value
        ? "border-success/40 bg-success/10 text-success-dark"
        : "border-error/40 bg-error/5 text-error-dark";
  const title =
    value === null
      ? t("access.defaults.unknownCell", { section, action })
      : value
        ? t("access.defaults.grantedTitle")
        : t("access.defaults.deniedTitle");
  const body = (
    <>
      <span aria-hidden="true">
        {value === null ? (
          <AlertTriangle size={12} strokeWidth={3} />
        ) : value ? (
          "✓"
        ) : (
          "✕"
        )}
      </span>
      <span>{label}</span>
    </>
  );
  const className = `flex items-center gap-1 rounded-md border px-2 py-1 font-mono text-xs ${tone}`;
  if (as === "span") {
    return (
      <span className={`${className} opacity-60`} title={title}>
        {body}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={`${className} transition-colors`}
      title={title}
      aria-pressed={value === true}
      onClick={onToggle}
    >
      {body}
    </button>
  );
}
